// ─── APP ROOT ─────────────────────────────────────────────────────────────────
// Root component: sets up React Query, React Router, and all route groups.
// Follows the spec: authenticated routes under AppLayout, public under AuthLayout.
// ─────────────────────────────────────────────────────────────────────────────

import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AppLayout } from './shared/components/AppLayout';
import { ProtectedRoute } from './shared/components/ProtectedRoute';
import { ProtectedPessoaRoute } from './shared/components/ProtectedPessoaRoute';

// Sprint 2.5.3 — cada página é seu próprio chunk (code-splitting por rota),
// carregado sob demanda em vez de tudo entrar no bundle principal. AppLayout/
// ProtectedRoute continuam eager (fazem parte do "shell" sempre necessário).
const LoginPage = lazy(() => import('./modules/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
// Item 1 do plano de segurança (auditoria 2026-09) — 2FA opt-in.
const MfaChallengePage = lazy(() => import('./modules/auth/pages/MfaChallengePage').then((m) => ({ default: m.MfaChallengePage })));
const MinhaContaPage = lazy(() => import('./modules/auth/pages/MinhaContaPage').then((m) => ({ default: m.MinhaContaPage })));
// Sprint 3.11 — página pública do imóvel (`/p/:publicToken`), 100% anônima
// (sem AppLayout/ProtectedRoute) — ver imovel-publico.* nesta mesma pasta.
const ImovelPublicoPage = lazy(() => import('./modules/imoveis/publico/ImovelPublicoPage').then((m) => ({ default: m.ImovelPublicoPage })));
// Sprint 3.8 — Área do Cliente: chunk próprio, nunca compartilha código com
// AppLayout/ProtectedRoute da equipe interna.
const AreaClienteLoginPage = lazy(() => import('./modules/area-cliente/pages/AreaClienteLoginPage').then((m) => ({ default: m.AreaClienteLoginPage })));
const AreaClienteLayout = lazy(() => import('./modules/area-cliente/components/AreaClienteLayout').then((m) => ({ default: m.AreaClienteLayout })));
const MinhasLocacoesPage = lazy(() => import('./modules/area-cliente/pages/MinhasLocacoesPage').then((m) => ({ default: m.MinhasLocacoesPage })));
const TrocarSenhaPage = lazy(() => import('./modules/area-cliente/pages/TrocarSenhaPage').then((m) => ({ default: m.TrocarSenhaPage })));
const MeuFinanceiroPage = lazy(() => import('./modules/area-cliente/pages/MeuFinanceiroPage').then((m) => ({ default: m.MeuFinanceiroPage })));
const DashboardPage = lazy(() => import('./modules/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const ImoveisListPage = lazy(() => import('./modules/imoveis/pages/ImoveisListPage').then((m) => ({ default: m.ImoveisListPage })));
const ImovelCreatePage = lazy(() => import('./modules/imoveis/pages/ImovelCreatePage').then((m) => ({ default: m.ImovelCreatePage })));
const ImovelEditPage = lazy(() => import('./modules/imoveis/pages/ImovelEditPage').then((m) => ({ default: m.ImovelEditPage })));
const ImovelViewPage = lazy(() => import('./modules/imoveis/pages/ImovelViewPage').then((m) => ({ default: m.ImovelViewPage })));
const PessoasListPage = lazy(() => import('./modules/pessoas/pages/PessoasListPage').then((m) => ({ default: m.PessoasListPage })));
const PessoaCreatePage = lazy(() => import('./modules/pessoas/pages/PessoaCreatePage').then((m) => ({ default: m.PessoaCreatePage })));
const PessoaDetailPage = lazy(() => import('./modules/pessoas/pages/PessoaDetailPage').then((m) => ({ default: m.PessoaDetailPage })));
const LeadsKanbanPage = lazy(() => import('./modules/leads/pages/LeadsKanbanPage').then((m) => ({ default: m.LeadsKanbanPage })));
const LeadsListPage = lazy(() => import('./modules/leads/pages/LeadsListPage').then((m) => ({ default: m.LeadsListPage })));
const LeadCreatePage = lazy(() => import('./modules/leads/pages/LeadCreatePage').then((m) => ({ default: m.LeadCreatePage })));
const LeadDetailPage = lazy(() => import('./modules/leads/pages/LeadDetailPage').then((m) => ({ default: m.LeadDetailPage })));
const LeadsDashboardPage = lazy(() => import('./modules/leads/pages/LeadsDashboardPage').then((m) => ({ default: m.LeadsDashboardPage })));
const LocacoesListPage = lazy(() => import('./modules/locacoes/pages/LocacoesListPage').then((m) => ({ default: m.LocacoesListPage })));
const LocacaoCreatePage = lazy(() => import('./modules/locacoes/pages/LocacaoCreatePage').then((m) => ({ default: m.LocacaoCreatePage })));
const LocacaoDetailPage = lazy(() => import('./modules/locacoes/pages/LocacaoDetailPage').then((m) => ({ default: m.LocacaoDetailPage })));
const LocacoesDashboardPage = lazy(() => import('./modules/locacoes/pages/LocacoesDashboardPage').then((m) => ({ default: m.LocacoesDashboardPage })));
const CasosListPage = lazy(() => import('./modules/juridico/pages/CasosListPage').then((m) => ({ default: m.CasosListPage })));
const CasoCreatePage = lazy(() => import('./modules/juridico/pages/CasoCreatePage').then((m) => ({ default: m.CasoCreatePage })));
const CasoDetailPage = lazy(() => import('./modules/juridico/pages/CasoDetailPage').then((m) => ({ default: m.CasoDetailPage })));
const JuridicoDashboardPage = lazy(() => import('./modules/juridico/pages/JuridicoDashboardPage').then((m) => ({ default: m.JuridicoDashboardPage })));
const AgendaCalendarioPage = lazy(() => import('./modules/agenda/pages/AgendaCalendarioPage').then((m) => ({ default: m.AgendaCalendarioPage })));
const AgendaTarefasPage = lazy(() => import('./modules/agenda/pages/AgendaTarefasPage').then((m) => ({ default: m.AgendaTarefasPage })));
const AgendaDashboardPage = lazy(() => import('./modules/agenda/pages/AgendaDashboardPage').then((m) => ({ default: m.AgendaDashboardPage })));
const CaixaEntradaPage = lazy(() => import('./modules/relacionamento/pages/CaixaEntradaPage').then((m) => ({ default: m.CaixaEntradaPage })));
const CaixaSaidaPage = lazy(() => import('./modules/relacionamento/pages/CaixaSaidaPage').then((m) => ({ default: m.CaixaSaidaPage })));
const TemplatesPage = lazy(() => import('./modules/relacionamento/pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })));
const ContasPage = lazy(() => import('./modules/relacionamento/pages/ContasPage').then((m) => ({ default: m.ContasPage })));
const RelacionamentoDashboardPage = lazy(() =>
  import('./modules/relacionamento/pages/RelacionamentoDashboardPage').then((m) => ({ default: m.RelacionamentoDashboardPage })),
);
const FinanceiroDashboardPage = lazy(() => import('./modules/financeiro/pages/FinanceiroDashboardPage').then((m) => ({ default: m.FinanceiroDashboardPage })));
const LancamentosListPage = lazy(() => import('./modules/financeiro/pages/LancamentosListPage').then((m) => ({ default: m.LancamentosListPage })));
const PlanoContasPage = lazy(() => import('./modules/financeiro/pages/PlanoContasPage').then((m) => ({ default: m.PlanoContasPage })));
const CentrosCustoPage = lazy(() => import('./modules/financeiro/pages/CentrosCustoPage').then((m) => ({ default: m.CentrosCustoPage })));
const CaixaListPage = lazy(() => import('./modules/financeiro/pages/CaixaListPage').then((m) => ({ default: m.CaixaListPage })));
const CaixaDetailPage = lazy(() => import('./modules/financeiro/pages/CaixaDetailPage').then((m) => ({ default: m.CaixaDetailPage })));
const ContasBancariasPage = lazy(() => import('./modules/financeiro/pages/ContasBancariasPage').then((m) => ({ default: m.ContasBancariasPage })));
const ConciliacaoPage = lazy(() => import('./modules/financeiro/pages/ConciliacaoPage').then((m) => ({ default: m.ConciliacaoPage })));
const LayoutsCsvPage = lazy(() => import('./modules/financeiro/pages/LayoutsCsvPage').then((m) => ({ default: m.LayoutsCsvPage })));
const ComissoesListPage = lazy(() => import('./modules/financeiro/pages/ComissoesListPage').then((m) => ({ default: m.ComissoesListPage })));
const NegociacoesKanbanPage = lazy(() => import('./modules/negociacoes/pages/NegociacoesKanbanPage').then((m) => ({ default: m.NegociacoesKanbanPage })));
const NegociacoesListPage = lazy(() => import('./modules/negociacoes/pages/NegociacoesListPage').then((m) => ({ default: m.NegociacoesListPage })));
const NegociacaoCreatePage = lazy(() => import('./modules/negociacoes/pages/NegociacaoCreatePage').then((m) => ({ default: m.NegociacaoCreatePage })));
const NegociacaoDetailPage = lazy(() => import('./modules/negociacoes/pages/NegociacaoDetailPage').then((m) => ({ default: m.NegociacaoDetailPage })));
const NegociacoesDashboardPage = lazy(() => import('./modules/negociacoes/pages/NegociacoesDashboardPage').then((m) => ({ default: m.NegociacoesDashboardPage })));
const VendasListPage = lazy(() => import('./modules/vendas/pages/VendasListPage').then((m) => ({ default: m.VendasListPage })));
const VendaCreatePage = lazy(() => import('./modules/vendas/pages/VendaCreatePage').then((m) => ({ default: m.VendaCreatePage })));
const VendaDetailPage = lazy(() => import('./modules/vendas/pages/VendaDetailPage').then((m) => ({ default: m.VendaDetailPage })));
const ComercialBiPage = lazy(() => import('./modules/comercial-bi/pages/ComercialBiPage').then((m) => ({ default: m.ComercialBiPage })));
const MetasPage = lazy(() => import('./modules/metas/pages/MetasPage').then((m) => ({ default: m.MetasPage })));
const RelatoriosHomePage = lazy(() => import('./modules/relatorios/pages/RelatoriosHomePage').then((m) => ({ default: m.RelatoriosHomePage })));
const RelatorioViewerPage = lazy(() => import('./modules/relatorios/pages/RelatorioViewerPage').then((m) => ({ default: m.RelatorioViewerPage })));
const ConfiguracoesHomePage = lazy(() => import('./modules/configuracoes/pages/ConfiguracoesHomePage').then((m) => ({ default: m.ConfiguracoesHomePage })));
const FiliaisPage = lazy(() => import('./modules/configuracoes/pages/FiliaisPage').then((m) => ({ default: m.FiliaisPage })));
const UsuariosPage = lazy(() => import('./modules/configuracoes/pages/UsuariosPage').then((m) => ({ default: m.UsuariosPage })));
const PerfisPage = lazy(() => import('./modules/configuracoes/pages/PerfisPage').then((m) => ({ default: m.PerfisPage })));
const PerfilDetailPage = lazy(() => import('./modules/configuracoes/pages/PerfilDetailPage').then((m) => ({ default: m.PerfilDetailPage })));
const EmpresaPage = lazy(() => import('./modules/configuracoes/pages/EmpresaPage').then((m) => ({ default: m.EmpresaPage })));
const ParametrizacoesPage = lazy(() => import('./modules/configuracoes/pages/ParametrizacoesPage').then((m) => ({ default: m.ParametrizacoesPage })));
const NumeracaoPage = lazy(() => import('./modules/configuracoes/pages/NumeracaoPage').then((m) => ({ default: m.NumeracaoPage })));
const TagsPage = lazy(() => import('./modules/configuracoes/pages/TagsPage').then((m) => ({ default: m.TagsPage })));
const NotificacoesConfigPage = lazy(() => import('./modules/configuracoes/pages/NotificacoesConfigPage').then((m) => ({ default: m.NotificacoesConfigPage })));
const PersonalizacaoPage = lazy(() => import('./modules/configuracoes/pages/PersonalizacaoPage').then((m) => ({ default: m.PersonalizacaoPage })));
const IntegracoesPage = lazy(() => import('./modules/configuracoes/pages/IntegracoesPage').then((m) => ({ default: m.IntegracoesPage })));
const ConhecimentoPage = lazy(() => import('./modules/configuracoes/pages/ConhecimentoPage').then((m) => ({ default: m.ConhecimentoPage })));

// Fallback mínimo enquanto o chunk da rota carrega — reaproveita a mesma
// classe `skeleton` já usada em todas as páginas durante isLoading, sem
// introduzir nenhum elemento visual novo.
function RouteFallback() {
  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="skeleton h-8 w-64 rounded" />
      <div className="skeleton h-64 w-full rounded-xl" />
    </div>
  );
}

// ── React Query client ────────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,          // 30 seconds
      gcTime: 5 * 60 * 1000,     // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ── Placeholder pages (stubs for Phase 1 — to be replaced in Phase 2+) ────────
function ComingSoonPage({ name }: { name: string }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center">
        <div className="w-16 h-16 bg-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">🚧</span>
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">{name}</h2>
        <p className="text-muted-foreground text-sm">
          Este módulo será desenvolvido na próxima fase.
        </p>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* ── Public routes ────────────────────────────────────────────── */}
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/forgot-password" element={<ComingSoonPage name="Recuperar Senha" />} />
          <Route path="/auth/reset-password" element={<ComingSoonPage name="Redefinir Senha" />} />
          {/* Etapa 2 do login pra quem já tem 2FA ativado — só alcançável via
              state de navegação vindo do LoginPage. */}
          <Route path="/auth/mfa-challenge" element={<MfaChallengePage />} />

          {/* Sprint 3.11 — página pública do imóvel, link que ShareImovelMenu.tsx
              já gerava desde antes (nunca existiu até agora). */}
          <Route path="/p/:publicToken" element={<ImovelPublicoPage />} />

          {/* ── Área do Cliente (Sprint 3.8) — árvore de rotas própria, ─────
              fora do AppLayout/ProtectedRoute da equipe interna. ─────────── */}
          <Route path="/area-cliente/login" element={<AreaClienteLoginPage />} />
          <Route
            path="/area-cliente"
            element={
              <ProtectedPessoaRoute>
                <AreaClienteLayout />
              </ProtectedPessoaRoute>
            }
          >
            <Route path="contratos" element={<MinhasLocacoesPage />} />
            <Route path="financeiro" element={<MeuFinanceiroPage />} />
            <Route path="trocar-senha" element={<TrocarSenhaPage />} />
          </Route>

          {/* ── Protected routes — all under AppLayout ───────────────────── */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="minha-conta" element={<MinhaContaPage />} />

            {/* Sprint 2.3 — Pessoas (entidade central do CRM) */}
            <Route path="pessoas" element={<Outlet />}>
              <Route index element={<PessoasListPage />} />
              <Route
                path="novo"
                element={
                  <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'assistente']}>
                    <PessoaCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route path=":id" element={<PessoaDetailPage />} />
            </Route>

            {/* Sprint 2.7 — CRM de Leads */}
            <Route
              path="leads"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'assistente']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<LeadsKanbanPage />} />
              <Route path="lista" element={<LeadsListPage />} />
              <Route path="dashboard" element={<LeadsDashboardPage />} />
              <Route path="novo" element={<LeadCreatePage />} />
              <Route path=":id" element={<LeadDetailPage />} />
            </Route>

            {/* Sprint 2.2 — Imóveis */}
            <Route
              path="imoveis"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'juridico', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<ImoveisListPage />} />
              <Route
                path="novo"
                element={
                  <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor']}>
                    <ImovelCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route path=":id" element={<ImovelViewPage />} />
              <Route
                path=":id/editar"
                element={
                  <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor']}>
                    <ImovelEditPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Sprint 3.0 — Agenda Inteligente e Central de Tarefas */}
            <Route
              path="agenda"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'assistente', 'juridico', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<AgendaCalendarioPage />} />
              <Route path="tarefas" element={<AgendaTarefasPage />} />
              <Route path="dashboard" element={<AgendaDashboardPage />} />
            </Route>

            {/* Sprint 2.8 — Gestão Completa de Locações */}
            <Route
              path="locacoes"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'juridico', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<LocacoesListPage />} />
              <Route path="dashboard" element={<LocacoesDashboardPage />} />
              <Route path="novo" element={<LocacaoCreatePage />} />
              <Route path=":id" element={<LocacaoDetailPage />} />
            </Route>

            {/* Sprint 3.3 — Gestão Comercial: Negociações (reaproveita RECURSOS.VENDAS) */}
            <Route
              path="negociacoes"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'juridico']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<NegociacoesKanbanPage />} />
              <Route path="lista" element={<NegociacoesListPage />} />
              <Route path="dashboard" element={<NegociacoesDashboardPage />} />
              <Route path="novo" element={<NegociacaoCreatePage />} />
              <Route path=":id" element={<NegociacaoDetailPage />} />
            </Route>

            {/* Sprint 3.3, Etapa 5 — Gestão Comercial: Vendas */}
            <Route
              path="vendas"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'juridico']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<VendasListPage />} />
              <Route path="novo" element={<VendaCreatePage />} />
              <Route path=":id" element={<VendaDetailPage />} />
            </Route>

            {/* Sprint 3.2 — Financeiro Empresarial */}
            <Route
              path="financeiro"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<FinanceiroDashboardPage />} />
              <Route path="lancamentos" element={<LancamentosListPage />} />
              <Route path="plano-contas" element={<PlanoContasPage />} />
              <Route path="centros-custo" element={<CentrosCustoPage />} />
              <Route path="caixa" element={<CaixaListPage />} />
              <Route path="caixa/:id" element={<CaixaDetailPage />} />
              <Route path="contas-bancarias" element={<ContasBancariasPage />} />
              <Route path="conciliacao" element={<ConciliacaoPage />} />
              <Route path="conciliacao/layouts-csv" element={<LayoutsCsvPage />} />
              <Route path="comissoes" element={<ComissoesListPage />} />
            </Route>

            {/* Sprint 2.9 — Gestão Jurídica Imobiliária */}
            <Route
              path="juridico"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'juridico']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<CasosListPage />} />
              <Route path="dashboard" element={<JuridicoDashboardPage />} />
              <Route path="novo" element={<CasoCreatePage />} />
              <Route path=":id" element={<CasoDetailPage />} />
            </Route>

            {/* Sprint 3.1 — Central de Relacionamento Omnichannel */}
            <Route
              path="relacionamento"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'assistente', 'juridico', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<CaixaEntradaPage />} />
              <Route path="saida" element={<CaixaSaidaPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="contas" element={<ContasPage />} />
              <Route path="dashboard" element={<RelacionamentoDashboardPage />} />
            </Route>

            {/* Sprint 3.4 — Central de Relatórios */}
            <Route
              path="relatorios"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'juridico', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<RelatoriosHomePage />} />
              <Route path=":id" element={<RelatorioViewerPage />} />
            </Route>

            {/* Sprint 3.6, Fase A — Inteligência Comercial / BI */}
            <Route
              path="comercial-bi"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<ComercialBiPage />} />
            </Route>
            <Route
              path="metas"
              element={
                <ProtectedRoute requiredPerfis={['admin', 'gestor', 'corretor', 'financeiro']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<MetasPage />} />
            </Route>

            {/* Sprint 3.5 — Administração do Sistema */}
            <Route
              path="configuracoes"
              element={
                <ProtectedRoute requiredPerfis={['admin']}>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<ConfiguracoesHomePage />} />
              <Route path="filiais" element={<FiliaisPage />} />
              <Route path="usuarios" element={<UsuariosPage />} />
              <Route path="perfis" element={<PerfisPage />} />
              <Route path="perfis/:id" element={<PerfilDetailPage />} />
              <Route path="empresa" element={<EmpresaPage />} />
              <Route path="parametrizacoes" element={<ParametrizacoesPage />} />
              <Route path="numeracao" element={<NumeracaoPage />} />
              <Route path="tags" element={<TagsPage />} />
              <Route path="notificacoes" element={<NotificacoesConfigPage />} />
              <Route path="personalizacao" element={<PersonalizacaoPage />} />
              <Route path="integracoes" element={<IntegracoesPage />} />
              <Route path="conhecimento" element={<ConhecimentoPage />} />
            </Route>
          </Route>

          {/* ── Catch-all: redirect to dashboard ─────────────────────────── */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>

      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
